import { type ReactNode, useCallback } from "react";
import type { GestureResponderEvent } from "react-native";
import { useLink } from "solito/link";
import { AnimatePresence, type ColorTokens, type GetProps, isWeb, styled, Theme, View, XStack } from "tamagui";
import { sizeTokens } from "../../themes/token-size";
import { HapticsService, useMinimumLoadingTime } from "../../utils";
import { Icon, type IconComponent } from "../Icon";
import { Loader } from "../Loader";
import { Interface1, Interface2, Interface3 } from "../Typography";
export type ButtonVariant = "primary" | "secondary" | "tertiary";
export type ButtonSize = "large" | "medium" | "small";
const buttonVariants = {
  variant: {
    primary: {
      backgroundColor: "$brandNavyDark",
      borderWidth: 0,
      hoverStyle: {
        backgroundColor: "$brandNavy"
      },
      pressStyle: {
        backgroundColor: "$brandNavy",
        scale: 0.97
      },
      focusVisibleStyle: {
        outlineWidth: 2,
        outlineColor: "$brandNavy",
        outlineStyle: "solid",
        outlineOffset: 2
      }
    },
    secondary: {
      backgroundColor: "$transparent",
      borderWidth: 1,
      borderColor: "$uiStroke",
      hoverStyle: {
        backgroundColor: "$transparent",
        borderColor: "$textPrimary"
      },
      pressStyle: {
        backgroundColor: "$transparent",
        borderColor: "$textPrimary",
        scale: 0.97
      },
      focusVisibleStyle: {
        outlineWidth: 2,
        outlineColor: "$brandNavy",
        outlineStyle: "solid",
        outlineOffset: 2
      }
    },
    tertiary: {
      backgroundColor: "$transparent",
      borderWidth: 0,
      hoverStyle: {
        backgroundColor: "$surfaceSecondary"
      },
      pressStyle: {
        backgroundColor: "$surfaceSecondary",
        scale: 0.97
      },
      focusVisibleStyle: {
        outlineWidth: 2,
        outlineColor: "$brandNavy",
        outlineStyle: "solid",
        outlineOffset: 2
      }
    }
  },
  size: {
    large: {
      height: "$si7",
      paddingHorizontal: "$sp4"
    },
    medium: {
      height: "$si5",
      paddingHorizontal: "$sp2.5"
    },
    small: {
      height: "$si4",
      paddingHorizontal: "$sp2"
    }
  },
  disabled: {
    true: {
      opacity: 1,
      backgroundColor: "$surfaceSecondary",
      borderColor: "$transparent",
      hoverStyle: {
        backgroundColor: "$surfaceSecondary",
        borderColor: "$transparent"
      },
      pressStyle: {
        backgroundColor: "$surfaceSecondary",
        borderColor: "$transparent",
        scale: 1
      }
    }
  },
  fillWidth: {
    true: {
      width: "100%"
    },
    false: {
      width: "auto",
      minWidth: "auto"
    }
  }
} as const;
const StyledButton = styled(View, {
  name: "Button",
  render: "button",
  role: "button",
  focusable: true,
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  borderRadius: "$brFull",
  // CSS transitions handle web; native uses Moti via animation prop on the instance
  style: {
    transitionProperty: "background-color, border-color, transform",
    transitionDuration: "100ms",
    transitionTimingFunction: "ease-out"
  },
  variants: buttonVariants,
  defaultVariants: {
    variant: "primary",
    size: "medium",
    fillWidth: true
  }
});
const opacityTransitionStyle = {
  transitionProperty: "opacity",
  transitionDuration: "100ms",
  transitionTimingFunction: "ease-out"
};

// Native-only animation props for opacity; on web, CSS transitions handle it.
const nativeOpacityAnimation = isWeb ? {} : {
  transition: "100ms easeOut" as const,
  animateOnly: ["opacity"]
};
const buttonTextStyles = {
  name: "ButtonText",
  style: opacityTransitionStyle
};
const ButtonTextLarge = styled(Interface1, buttonTextStyles);
const ButtonTextMedium = styled(Interface2, buttonTextStyles);
const ButtonTextSmall = styled(Interface3, buttonTextStyles);
const IconWrapper = styled(View, {
  name: "IconWrapper",
  style: opacityTransitionStyle
});

// Overlay that fades in/out with AnimatePresence when the button is loading.
// Absolute so it covers the hidden (opacity: 0) text/icons without affecting layout.
const LoaderOverlay = styled(View, {
  name: "LoaderOverlay",
  position: "absolute",
  inset: 0,
  justifyContent: "center",
  alignItems: "center",
  opacity: 1,
  enterStyle: {
    opacity: 0
  },
  exitStyle: {
    opacity: 0
  },
  style: opacityTransitionStyle
});
const textColorTokens: Record<ButtonVariant, ColorTokens> = {
  primary: "$surfaceWhite",
  secondary: "$textPrimary",
  tertiary: "$brandNavyDark"
};
const buttonSizeToIconToken: Record<ButtonSize, `$icon${Capitalize<ButtonSize>}`> = {
  large: "$iconLarge",
  medium: "$iconMedium",
  small: "$iconSmall"
};
const iconPaddingBySize = {
  large: {
    iconSide: "$sp3",
    textSide: "$sp4"
  },
  medium: {
    iconSide: "$sp1.5",
    textSide: "$sp2.5"
  },
  small: {
    iconSide: "$sp1",
    textSide: "$sp2"
  }
} as const;
type StyledButtonProps = GetProps<typeof StyledButton>;
type BaseButtonProps = Omit<StyledButtonProps, "variant" | "size" | "onPressOut" | "children" | "onPress" | "href" | "render" | "role"> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /**
   * Skip the minimum loading display time (400ms) and transition instantly.
   * @default false
   */
  instantLoading?: boolean;
};

/**
 * Icon-only buttons: pass `aria-label` for screen readers.
 * Omit only for purely decorative icons.
 */
type IconOnlyButtonProps = BaseButtonProps & {
  icon: IconComponent;
  children?: never;
  iconLeft?: never;
  iconRight?: never;
};
type TextButtonProps = BaseButtonProps & {
  icon?: never;
  children: ReactNode;
  iconLeft?: IconComponent;
  iconRight?: IconComponent;
};
type VisualProps = IconOnlyButtonProps | TextButtonProps;

/** Action button — calls `onPress` when tapped. Renders `<button>` on web. */
type ActionProps = {
  onPress?: (event: GestureResponderEvent) => void;
  href?: never;
  replace?: never;
  scroll?: never;
  shallow?: never;
};

/** Link button — navigates to `href` via Solito. Renders `<a>` on web. */
type LinkProps = {
  /** URL to navigate to. Uses Solito for cross-platform routing. */
  href: string;
  /** Replace the current history entry instead of pushing. */
  replace?: boolean;
  /** Scroll to top on navigation (web). @default true */
  scroll?: boolean;
  /** Perform a shallow route change (web). */
  shallow?: boolean;
  /**
   * Optional callback for analytics or other side effects when the link is clicked.
   * Called before navigation. Must NOT call event.preventDefault() or navigation will not occur.
   */
  onPress?: (event: GestureResponderEvent) => void;
};
type BehaviorProps = ActionProps | LinkProps;
export type ButtonProps = VisualProps & BehaviorProps;

// Square sizes for icon-only buttons (width = height)
const iconOnlySizes: Record<ButtonSize, number> = {
  large: sizeTokens.si7,
  medium: sizeTokens.si5,
  small: sizeTokens.si4
};
export const Button = (props: ButtonProps) => {
  const {
    variant = "primary",
    size = "medium",
    loading,
    instantLoading = false,
    disabled,
    icon,
    iconLeft,
    iconRight,
    fillWidth = true,
    children,
    onPress,
    href,
    replace,
    scroll,
    shallow,
    ...rest
  } = props;
  const isIconOnly = icon !== undefined;
  const solitoLinkProps = useLink({
    href: href ?? "/",
    replace,
    scroll,
    shallow
  });
  const debouncedLoading = useMinimumLoadingTime(loading);
  const showLoading = instantLoading ? loading ?? false : debouncedLoading;
  const isInteractionDisabled = disabled || showLoading;
  const resolvedOnPress = useCallback((event: GestureResponderEvent) => {
    if (href) {
      onPress?.(event);
      solitoLinkProps.onPress?.(event);
    } else {
      onPress?.(event);
    }
  }, [href, onPress, solitoLinkProps.onPress]);
  const handlePressOut = useCallback(() => {
    if (!isInteractionDisabled) {
      void HapticsService.selection();
    }
  }, [isInteractionDisabled]);
  const handlePress = useCallback((event: GestureResponderEvent) => {
    if (!isInteractionDisabled) {
      resolvedOnPress?.(event);
    }
  }, [isInteractionDisabled, resolvedOnPress]);
  const buttonTextBySize = {
    large: ButtonTextLarge,
    medium: ButtonTextMedium,
    small: ButtonTextSmall
  };
  const ButtonText = buttonTextBySize[size];
  const iconSize = buttonSizeToIconToken[size];
  const textColor: ColorTokens = disabled ? "$textDisabled" : textColorTokens[variant];

  // For icon-only buttons, make width equal to height (square) and remove padding
  const iconOnlyStyles = isIconOnly ? {
    width: iconOnlySizes[size],
    paddingHorizontal: 0
  } : {};

  // Asymmetric padding when icons present (optically centered per design spec).
  // Use reduced padding on whichever side has an icon.
  const hasIcons = !isIconOnly && (iconLeft || iconRight);
  const iconPaddingStyles = hasIcons ? {
    paddingLeft: iconLeft ? iconPaddingBySize[size].iconSide : iconPaddingBySize[size].textSide,
    paddingRight: iconRight ? iconPaddingBySize[size].iconSide : iconPaddingBySize[size].textSide
  } : {};
  return <Theme name="v2" data-sentry-element="Theme" data-sentry-component="Button" data-sentry-source-file="Button.tsx">
      <StyledButton {...rest} render={href ? "a" : "button"} role={href ? "link" : "button"} {...href && isWeb && !isInteractionDisabled ? {
      href
    } : {}} variant={variant} size={size} disabled={disabled} fillWidth={isIconOnly ? false : fillWidth} onPressOut={handlePressOut} onPress={handlePress} {...iconOnlyStyles} {...iconPaddingStyles} {...isInteractionDisabled && {
      cursor: "not-allowed"
    }} {...!isWeb && {
      // TODO: Re-enable transition/animateOnly props once background color animation
      // is fixed (GRO-474).
    }} data-sentry-element="StyledButton" data-sentry-source-file="Button.tsx">
        <XStack justifyContent="center" alignItems="center" gap="$sp1" position="relative" data-sentry-element="XStack" data-sentry-source-file="Button.tsx">
          {icon ? <IconWrapper opacity={showLoading ? 0 : 1} {...nativeOpacityAnimation} style={{
          outline: "none",
          pointerEvents: "none"
        }}>
              <Icon icon={icon} size={iconSize} color={textColor} />
            </IconWrapper> : null}
          {iconLeft ? <IconWrapper opacity={showLoading ? 0 : 1} {...nativeOpacityAnimation}>
              <Icon icon={iconLeft} size={iconSize} color={textColor} />
            </IconWrapper> : null}
          {children ? <ButtonText color={textColor} opacity={showLoading ? 0 : 1} {...nativeOpacityAnimation}>
              {children}
            </ButtonText> : null}
          {iconRight ? <IconWrapper opacity={showLoading ? 0 : 1} {...nativeOpacityAnimation}>
              <Icon icon={iconRight} size={iconSize} color={textColor} />
            </IconWrapper> : null}
          <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-source-file="Button.tsx">
            {showLoading ? <LoaderOverlay {...nativeOpacityAnimation}>
                <Loader color={textColor} size={iconSize} />
              </LoaderOverlay> : null}
          </AnimatePresence>
        </XStack>
      </StyledButton>
    </Theme>;
};